import { Capacitor } from "@capacitor/core";
import { withHandoffParams } from "@/lib/analytics/session";

export type InAppBrowserType =
  | "kakaotalk"
  | "line"
  | "naver"
  | "instagram"
  | "facebook"
  | "threads"
  | "twitter"
  | "daum"
  | "android-webview"
  | "unknown";

/**
 * 인앱 브라우저(KakaoTalk, LINE, Instagram, Facebook, Threads, Twitter/X, Naver 등) 감지.
 * Locarora 자체 네이티브 앱(Capacitor)은 제외.
 */
export function isInAppBrowser(): boolean {
  return getInAppBrowserType() !== "unknown";
}

export function getInAppBrowserType(): InAppBrowserType {
  if (typeof navigator === "undefined") return "unknown";
  if (Capacitor.isNativePlatform()) return "unknown";

  const ua = navigator.userAgent || "";

  if (/KAKAOTALK/i.test(ua)) return "kakaotalk";
  if (/Line\//i.test(ua)) return "line";
  if (/NAVER\(inapp/i.test(ua)) return "naver";
  if (/Instagram/i.test(ua)) return "instagram";
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "facebook";
  // Threads는 internal 코드네임 "Barcelona" 포함, 또는 명시적 "Threads"
  if (/Barcelona|Threads/i.test(ua)) return "threads";
  if (/Twitter/i.test(ua)) return "twitter";
  if (/DaumApps/i.test(ua)) return "daum";
  // 일반 Android WebView fallback (위 케이스 외)
  if (/; wv\)/i.test(ua)) return "android-webview";

  return "unknown";
}

export function detectPlatform(): "ios" | "android" | "other" {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "other";
}

export function isKakaoTalk(): boolean {
  return getInAppBrowserType() === "kakaotalk";
}

/**
 * PWA(홈 화면에 추가)로 standalone 모드에서 실행 중인지 감지.
 * standalone 모드에서 OAuth는 외부 시스템 브라우저로 빠지면 PWA로 자연 복귀가
 * 어려움 — 사용자에게 별도 안내 필요.
 */
export function isStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari 전용 비표준 속성
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

/**
 * iOS Apple 정책상 외부 브라우저 강제 이동이 불가능한 인앱 브라우저.
 * (Instagram, Facebook, Threads, Twitter/X — Meta + 기타)
 * 이런 환경에서는 사용자 안내 모달로 fallback해야 함.
 */
export function isMetaInAppBrowser(): boolean {
  const type = getInAppBrowserType();
  return (
    type === "instagram" ||
    type === "facebook" ||
    type === "threads" ||
    type === "twitter"
  );
}

/**
 * Android 시스템 브라우저(Chrome, Samsung Internet, Firefox 등)로 이동.
 * 패키지 미지정 → OS의 브라우저 선택 다이얼로그 표시 (사용자 기본 브라우저 존중).
 */
export function buildAndroidIntentUrl(currentUrl: string): string {
  const url = new URL(currentUrl);
  const scheme = url.protocol.replace(":", "");
  return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=${scheme};end`;
}

/**
 * @deprecated buildAndroidIntentUrl 사용 — Chrome 패키지 강제 시 삼성 인터넷 사용자 비존중.
 */
export function buildChromeIntentUrl(currentUrl: string): string {
  const url = new URL(currentUrl);
  const scheme = url.protocol.replace(":", "");
  return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=${scheme};package=com.android.chrome;end`;
}

/**
 * KakaoTalk iOS 전용 스킴 — 우측 하단 메뉴 거치지 않고 외부 Safari 강제 오픈.
 */
export function buildKakaoTalkOpenExternalUrl(currentUrl: string): string {
  return `kakaotalk://web/openExternal?url=${encodeURIComponent(currentUrl)}`;
}

/**
 * LINE iOS/Android 공식 우회 — URL에 `openExternalBrowser=1` 쿼리 부착 시
 * LINE 앱이 자동으로 시스템 브라우저로 이동시킴.
 * 이미 부착된 경우 동일 URL 반환.
 */
export function buildLineExternalUrl(currentUrl: string): string {
  const url = new URL(currentUrl);
  if (url.searchParams.get("openExternalBrowser") === "1") return currentUrl;
  url.searchParams.set("openExternalBrowser", "1");
  return url.toString();
}

/**
 * Naver 앱 인앱 브라우저 종료 + 시스템 브라우저 오픈 (비공식 스킴).
 * 일부 버전에서 동작하지 않을 수 있음 — fallback 안내 필요.
 */
export function buildNaverExternalUrl(): string {
  return "naversearchapp://inappbrowser/close?autoOpenType=2";
}

export interface OpenExternalPlan {
  /**
   * "auto-redirect": 코드 한 줄로 시스템 브라우저로 이동 가능
   * "manual-guide": 코드 강제 이동 불가, 사용자에게 풀스크린 가이드 모달 노출 필요
   */
  type: "auto-redirect" | "manual-guide";
  /** auto-redirect용 URL */
  url?: string;
}

/**
 * 현재 환경에서 시스템 브라우저로 이동시킬 최적의 방법을 반환.
 * 인앱 브라우저가 아니면 null.
 */
export function getOpenExternalPlan(currentUrl: string): OpenExternalPlan | null {
  const type = getInAppBrowserType();
  if (type === "unknown") return null;

  const platform = detectPlatform();
  // 외부 브라우저가 같은 방문자(la_vid)·유입 출처를 이어받도록 — 스킴으로 감싸기 전
  // 이 한 곳에서만 붙인다. Naver 스킴은 URL 을 싣지 못해 해당 없음.
  const targetUrl = withHandoffParams(currentUrl, type);

  if (platform === "android") {
    return { type: "auto-redirect", url: buildAndroidIntentUrl(targetUrl) };
  }

  if (platform === "ios") {
    if (type === "kakaotalk") {
      return {
        type: "auto-redirect",
        url: buildKakaoTalkOpenExternalUrl(targetUrl),
      };
    }
    if (type === "line") {
      return { type: "auto-redirect", url: buildLineExternalUrl(targetUrl) };
    }
    if (type === "naver") {
      return { type: "auto-redirect", url: buildNaverExternalUrl() };
    }
    // iOS Instagram/Facebook/Threads/Twitter — Apple 정책상 강제 이동 불가
    return { type: "manual-guide" };
  }

  return { type: "manual-guide" };
}

/**
 * Clipboard API 사용 + 미지원 인앱 브라우저용 execCommand fallback.
 * 구현은 `@/lib/utils/clipboard`로 이동 — 기존 import 경로 호환을 위해 재수출.
 */
export { copyToClipboard } from "./clipboard";
